import React, { useEffect, useState } from 'react';
import {
  adminListPilotRequests, adminUpdatePilotRequest,
  adminListOrgs, adminCreateOrg, adminUpdateOrgStatus,
  adminListUsers, adminInviteUser, adminActivity,
} from '../services/admin';
import { useAuth } from '../contexts/AuthContext';
import { useConfig } from '../contexts/ConfigContext';
import {
  Shield, Building2, Users, Activity, Inbox,
  CheckCircle2, XCircle, Mail, Plus, Copy, Loader2,
} from 'lucide-react';

const TABS = [
  { id: 'pilots', label: 'Pilot Requests', icon: Inbox },
  { id: 'orgs', label: 'Organizations', icon: Building2 },
  { id: 'users', label: 'Users', icon: Users },
  { id: 'flags', label: 'Feature Flags', icon: Shield },
  { id: 'activity', label: 'Activity', icon: Activity },
];

export default function Admin() {
  const [tab, setTab] = useState('pilots');
  const { profile } = useAuth();

  return (
    <div className="space-y-6" data-testid="admin-page">
      <div className="rounded-xl border border-cyan-glow/20 bg-cyan-glow/5 p-5 flex items-start gap-4">
        <div className="h-10 w-10 rounded-lg bg-cyan-glow/10 border border-cyan-glow/30 grid place-items-center text-cyan-glow shrink-0">
          <Shield className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="font-display text-xl font-bold tracking-tight text-white">Internal Operations Console</h2>
          <p className="text-sm text-slate-400 mt-1">Super-admin · {profile?.email || profile?.user_id?.slice(0, 8)}</p>
        </div>
      </div>

      <div className="border-b border-white/10">
        <div className="flex gap-1 overflow-x-auto">
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`admin-tab-${t.id}`}
              className={`inline-flex items-center gap-2 px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                tab === t.id ? 'border-cyan-glow text-white' : 'border-transparent text-slate-400 hover:text-white'
              }`}>
              <t.icon className="h-4 w-4" />
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {tab === 'pilots' && <PilotRequestsTab />}
      {tab === 'orgs' && <OrgsTab />}
      {tab === 'users' && <UsersTab />}
      {tab === 'flags' && <FlagsTab />}
      {tab === 'activity' && <ActivityTab />}
    </div>
  );
}

function PilotRequestsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const reload = () => {
    setLoading(true);
    adminListPilotRequests().then(setItems).finally(() => setLoading(false));
  };
  useEffect(reload, []);

  const updateStatus = async (id, statusValue) => {
    await adminUpdatePilotRequest(id, { status: statusValue });
    reload();
  };

  if (loading) return <div className="text-sm text-slate-400" data-testid="pilots-loading">Loading…</div>;
  if (!items.length) return (
    <div className="rounded-xl border border-white/10 bg-navy-900 p-10 text-center" data-testid="pilots-empty">
      <Inbox className="h-8 w-8 text-slate-500 mx-auto mb-3" />
      <div className="font-display text-lg text-white">No pilot requests yet</div>
      <p className="text-sm text-slate-400 mt-1">Submissions from the public landing page will appear here.</p>
    </div>
  );

  return (
    <div className="space-y-3" data-testid="pilots-list">
      {items.map(r => (
        <div key={r.id} className="rounded-xl border border-white/10 bg-navy-900 p-5" data-testid={`pilot-row-${r.id}`}>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="font-display font-semibold text-white">{r.company_name}</span>
                <StatusPill status={r.status} />
              </div>
              <div className="text-sm text-slate-400">{r.contact_name} · <span className="font-mono text-xs">{r.email}</span></div>
              <div className="text-xs text-slate-500 mt-1 font-mono">{r.company_type} · {r.country}</div>
              {r.message && <p className="text-sm text-slate-300 mt-3 leading-relaxed bg-navy-950/40 p-3 rounded-md border border-white/5">{r.message}</p>}
            </div>
            <div className="flex flex-col sm:flex-row gap-2 shrink-0">
              <button onClick={() => updateStatus(r.id, 'approved')}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 hover:bg-emerald-500/20 transition-colors"
                data-testid={`approve-${r.id}`}>
                <CheckCircle2 className="h-3.5 w-3.5" /> Approve
              </button>
              <button onClick={() => updateStatus(r.id, 'contacted')}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs bg-cyan-glow/10 border border-cyan-glow/20 text-cyan-glow hover:bg-cyan-glow/20 transition-colors">
                <Mail className="h-3.5 w-3.5" /> Mark contacted
              </button>
              <button onClick={() => updateStatus(r.id, 'rejected')}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs bg-red-500/10 border border-red-500/20 text-red-400 hover:bg-red-500/20 transition-colors"
                data-testid={`reject-${r.id}`}>
                <XCircle className="h-3.5 w-3.5" /> Reject
              </button>
            </div>
          </div>
          <div className="mt-3 text-[10px] text-slate-600 font-mono">Submitted {new Date(r.created_at).toLocaleString()}</div>
        </div>
      ))}
    </div>
  );
}

function OrgsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: '', slug: '' });
  const [creating, setCreating] = useState(false);

  const reload = () => { setLoading(true); adminListOrgs().then(setItems).finally(() => setLoading(false)); };
  useEffect(reload, []);

  const create = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await adminCreateOrg({ name: form.name, slug: form.slug });
      setForm({ name: '', slug: '' });
      reload();
    } finally { setCreating(false); }
  };

  return (
    <div className="space-y-5" data-testid="orgs-tab">
      <form onSubmit={create} className="rounded-xl border border-white/10 bg-navy-900 p-5 grid grid-cols-1 md:grid-cols-3 gap-3 items-end" data-testid="org-create-form">
        <Field label="Organization name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="org-name" />
        <Field label="Slug (lowercase, dashes)" value={form.slug} onChange={(v) => setForm({ ...form, slug: v.toLowerCase().replace(/[^a-z0-9-]/g, '-') })} testid="org-slug" />
        <button type="submit" disabled={creating || !form.name || !form.slug}
          className="px-4 py-2.5 rounded-lg bg-cyan-glow text-navy-950 font-semibold hover:bg-cyan-hover disabled:opacity-60 inline-flex items-center justify-center gap-1.5"
          data-testid="org-create-btn">
          <Plus className="h-4 w-4" /> Create organization
        </button>
      </form>

      {loading ? <div className="text-sm text-slate-400">Loading…</div> : (
        <div className="rounded-xl border border-white/10 bg-navy-900 overflow-hidden">
          {items.length === 0 ? (
            <div className="p-8 text-center text-sm text-slate-400">No organizations yet.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-navy-800/50 border-b border-white/5">
                <tr className="text-left text-xs text-slate-400 uppercase tracking-wider font-mono">
                  <th className="px-5 py-3">Name</th><th className="px-5 py-3">Slug</th>
                  <th className="px-5 py-3">Status</th><th className="px-5 py-3">Created</th><th></th>
                </tr>
              </thead>
              <tbody>
                {items.map(o => (
                  <tr key={o.id} className="border-b border-white/5 last:border-0" data-testid={`org-row-${o.slug}`}>
                    <td className="px-5 py-3 font-medium">{o.name}</td>
                    <td className="px-5 py-3 font-mono text-xs text-slate-400">{o.slug}</td>
                    <td className="px-5 py-3"><StatusPill status={o.status} /></td>
                    <td className="px-5 py-3 text-xs text-slate-500 font-mono">{new Date(o.created_at).toLocaleDateString()}</td>
                    <td className="px-5 py-3 text-right">
                      <button onClick={async () => { await adminUpdateOrgStatus(o.id, o.status === 'active' ? 'inactive' : 'active'); reload(); }}
                        className="text-xs text-cyan-glow hover:text-cyan-hover">
                        {o.status === 'active' ? 'Deactivate' : 'Activate'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

function UsersTab() {
  const [users, setUsers] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [form, setForm] = useState({ email: '', organization_id: '', role: 'org_user', full_name: '' });
  const [inviting, setInviting] = useState(false);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const activeOrgs = orgs.filter((org) => org.status === 'active');
  const hasActiveOrganization = activeOrgs.some((org) => org.id === form.organization_id);

  const reload = async () => {
    setLoading(true);
    try { const [u, o] = await Promise.all([adminListUsers(), adminListOrgs()]); setUsers(u); setOrgs(o); }
    finally { setLoading(false); }
  };
  useEffect(() => { reload(); }, []);

  const invite = async (e) => {
    e.preventDefault();
    if (!hasActiveOrganization) {
      setResult({ error: 'Select an active organization before inviting a user.' });
      return;
    }

    setInviting(true); setResult(null);
    try {
      const r = await adminInviteUser(form);
      setResult(r);
      setForm({ email: '', organization_id: form.organization_id, role: 'org_user', full_name: '' });
      reload();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const errorMessage = typeof detail === 'string' ? detail : detail?.message || err.message;
      setResult({ error: errorMessage });
    } finally { setInviting(false); }
  };

  return (
    <div className="space-y-5" data-testid="users-tab">
      <form onSubmit={invite} className="rounded-xl border border-white/10 bg-navy-900 p-5 grid grid-cols-1 md:grid-cols-4 gap-3 items-end" data-testid="invite-form">
        <Field label="Email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} type="email" testid="invite-email" />
        <Field label="Full name (optional)" value={form.full_name} onChange={(v) => setForm({ ...form, full_name: v })} testid="invite-name" />
        <label className="block">
          <span className="text-xs text-slate-400 uppercase tracking-wider font-mono">Organization</span>
          <select value={form.organization_id} onChange={(e) => setForm({ ...form, organization_id: e.target.value })} required
            className="mt-2 w-full bg-navy-950 border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm" data-testid="invite-org">
            <option value="">Select…</option>
            {activeOrgs.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
        </label>
        <button type="submit" disabled={inviting || !form.email || !hasActiveOrganization}
          className="px-4 py-2.5 rounded-lg bg-cyan-glow text-navy-950 font-semibold hover:bg-cyan-hover disabled:opacity-60 inline-flex items-center justify-center gap-1.5"
          data-testid="invite-submit-btn">
          {inviting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
          Send invite
        </button>
      </form>

      {result && (
        <div className={`rounded-xl border p-4 text-sm ${
          result.error
            ? 'border-red-500/30 bg-red-500/10 text-red-300'
            : result.sent === false
              ? 'border-amber-500/30 bg-amber-500/10 text-amber-200'
              : 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300'
        }`} data-testid="invite-result">
          {result.error ? result.error : (
            <div className="flex items-start gap-2">
              <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" />
              <div>
                <div>{result.sent === false ? 'Workspace access updated. User can sign in or reset password.' : 'Invite sent successfully.'}</div>
                <div className="text-xs mt-1 opacity-80">
                  {result.sent === false ? 'Account update prepared for ' : 'Invitation sent to '}
                  <span className="font-mono">{result.email}</span>
                </div>
                {result.note && <div className="text-xs mt-1 opacity-80">{result.note}</div>}
                {typeof result.invite_link === 'string' && /^https?:\/\//.test(result.invite_link) && (
                  <button onClick={() => navigator.clipboard.writeText(result.invite_link)} className="text-xs mt-2 inline-flex items-center gap-1 underline">
                    <Copy className="h-3 w-3" /> Copy invite link
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {loading ? <div className="text-sm text-slate-400">Loading users…</div> : (
        <div className="rounded-xl border border-white/10 bg-navy-900 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-navy-800/50 border-b border-white/5">
              <tr className="text-left text-xs text-slate-400 uppercase tracking-wider font-mono">
                <th className="px-5 py-3">User</th><th className="px-5 py-3">Role</th>
                <th className="px-5 py-3">Organization</th><th className="px-5 py-3">Joined</th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => {
                const org = orgs.find(o => o.id === u.organization_id);
                return (
                  <tr key={u.id} className="border-b border-white/5 last:border-0" data-testid={`user-row-${u.id}`}>
                    <td className="px-5 py-3">
                      <div className="font-medium text-white">{u.full_name || '—'}</div>
                      <div className="text-xs font-mono text-slate-500">{u.id.slice(0, 8)}…</div>
                    </td>
                    <td className="px-5 py-3"><RolePill role={u.role} /></td>
                    <td className="px-5 py-3 text-slate-300">{org ? org.name : <span className="text-slate-500 italic">—</span>}</td>
                    <td className="px-5 py-3 text-xs text-slate-500 font-mono">{u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function FlagsTab() {
  const { config } = useConfig();
  const flags = [
    { key: 'enable_login', label: 'Login', desc: 'Allow users to access the /login sign-in screen.' },
    { key: 'enable_public_auth', label: 'Public auth', desc: 'Show public Login/Signup CTAs.' },
    { key: 'enable_self_signup', label: 'Self signup', desc: 'Allow direct signup without invite.' },
    { key: 'enable_pilot_requests', label: 'Pilot requests', desc: 'Accept pilot access requests from the landing page.' },
  ];

  return (
    <div className="space-y-3" data-testid="flags-tab">
      <div className="rounded-xl border border-white/10 bg-navy-900 p-5">
        <div className="text-xs uppercase tracking-widest text-slate-500 font-mono mb-4">Backend feature flags</div>
        <div className="space-y-3">
          {flags.map(f => (
            <div key={f.key} className="flex items-start justify-between gap-4 pb-3 border-b border-white/5 last:border-0">
              <div className="min-w-0">
                <div className="font-medium text-white">{f.label}</div>
                <div className="text-xs text-slate-400 mt-1">{f.desc}</div>
                <div className="text-[10px] text-slate-600 font-mono mt-1.5">{f.key.toUpperCase()}</div>
              </div>
              <div className={`px-3 py-1 rounded-full text-xs ${config?.[f.key] ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : 'bg-amber-500/10 text-amber-400 border border-amber-500/20'}`}>
                {config?.[f.key] ? 'true' : 'false'}
              </div>
            </div>
          ))}
        </div>
        <div className="mt-4 text-[11px] text-slate-500 font-mono">
          Edit <code>backend/.env</code> and restart the backend to change. Flags read on every page load via <code>/api/config/public</code>.
        </div>
      </div>
    </div>
  );
}

function ActivityTab() {
  const [data, setData] = useState(null);
  useEffect(() => { adminActivity().then(setData); }, []);
  if (!data) return <div className="text-sm text-slate-400">Loading…</div>;
  const stats = [
    { label: 'Pilot requests', value: data.pilot_requests_total, sub: `${data.pilot_requests_pending} pending` },
    { label: 'Organizations', value: data.organizations_total },
    { label: 'Users', value: data.users_total },
    { label: 'Shipments', value: data.shipments_total, sub: `${data.shipments_blocking} blocking` },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="activity-tab">
      {stats.map(s => (
        <div key={s.label} className="rounded-xl border border-white/10 bg-navy-900 p-5">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">{s.label}</div>
          <div className="font-display text-3xl font-bold text-white mt-2">{s.value}</div>
          {s.sub && <div className="text-[11px] text-cyan-glow mt-2 font-mono">{s.sub}</div>}
        </div>
      ))}
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    pending: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
    approved: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
    rejected: 'bg-red-500/10 text-red-400 border-red-500/20',
    contacted: 'bg-cyan-glow/10 text-cyan-glow border-cyan-glow/20',
    active: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
    inactive: 'bg-slate-500/10 text-slate-300 border-slate-500/20',
  };
  return <span className={`text-[10px] uppercase tracking-widest font-mono px-2 py-0.5 rounded-full border ${map[status] || ''}`}>{status}</span>;
}

function RolePill({ role }) {
  const map = {
    super_admin: 'bg-cyan-glow/10 text-cyan-glow border-cyan-glow/20',
    org_admin: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
    org_user: 'bg-slate-500/10 text-slate-300 border-slate-500/20',
  };
  return <span className={`text-[10px] uppercase tracking-widest font-mono px-2 py-0.5 rounded-full border ${map[role] || map.org_user}`}>{role.replace('_', ' ')}</span>;
}

function Field({ label, value, onChange, type = 'text', testid }) {
  return (
    <label className="block">
      <span className="text-xs text-slate-400 uppercase tracking-wider font-mono">{label}</span>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)}
        className="mt-2 w-full bg-navy-950 border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm focus:border-cyan-glow/50 focus:outline-none focus:ring-2 focus:ring-cyan-glow/20"
        data-testid={testid} />
    </label>
  );
}

// (useConfig is imported at top)
